import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import { getMyProfile } from "../../../../core/data/redux/employeesSlice";
import { getUserLoginData } from "../../../../core/data/redux/authSlice";
import { toast } from "react-toastify";
import { getUserStaffQuarterRequests, submitStaffQuarterOccupation, getStaffQuarterOccupationWithdrawalList, withdrawStaffQuarterOccupation, getStaffQuarterWithdrawalDetails, getStaffQuarterOccupationDetails, getMyAllotments, getStaffQuarterCurrentRegistration, generateDeclarationPdf, generateOccupationReportPdf, getEmployeeDetailsForStaffQuarter, uploadDeclarationSignature } from "../../../../core/data/redux/staffQuarterSlice";
import { Typography, DatePicker } from "antd";
import dayjs from "dayjs";
import { encodeRouteParam } from "../../../../core/hooks/routeEncoder";
import { formatFlatNoWithBlock, resolveFlatCategory } from "../staffQuarterUtils";

const { Text } = Typography;

const OccupationWithdrawal: React.FC = () => {
  const routes = all_routes as any;
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();

  const employeesState = useSelector((s: any) => s.employeesState);
  const staffQuarterState = useSelector((state: any) => state.staffQuarterState);
  const authState = useSelector((state: any) => state.authState);
  const user = employeesState?.data?.data;

  const localUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  const getCategoryVal = (obj: any): string => {
    if (!obj || typeof obj !== "object") return "";

    const extractString = (val: any): string => {
      if (!val) return "";
      if (typeof val === "string" || typeof val === "number") return String(val).trim();
      if (typeof val === "object") {
        return (
          extractString(val.category) ||
          extractString(val.flatCategory) ||
          extractString(val.flat_category) ||
          extractString(val.name) ||
          extractString(val.categoryName) ||
          extractString(val.category_name) ||
          extractString(val.code) ||
          ""
        );
      }
      return "";
    };

    return (
      extractString(obj.category) ||
      extractString(obj.flat?.category) ||
      extractString(obj.flatDetails?.category) ||
      extractString(obj.allotmentDetails?.category) ||
      extractString(obj.categoryName) ||
      extractString(obj.category_name) ||
      extractString(obj.empCategory) ||
      extractString(obj.emp_category) ||
      extractString(obj.employeeCategory) ||
      extractString(obj.casteCategory) ||
      extractString(obj.caste) ||
      extractString(obj.catName) ||
      extractString(obj.cat_name) ||
      ""
    );
  };

  let userRequests: any[] = [];
  if (Array.isArray(staffQuarterState?.requests)) {
    userRequests = staffQuarterState.requests;
  } else if (Array.isArray(staffQuarterState?.requests?.data)) {
    userRequests = staffQuarterState.requests.data;
  } else if (Array.isArray(staffQuarterState?.requests?.data?.requests)) {
    userRequests = staffQuarterState.requests.data.requests;
  }
  const approvedRequests = userRequests.filter((req: any) => req.status === "APPROVED");
  const occupationRequests = staffQuarterState?.occupations?.data || [];
  const allotments = staffQuarterState?.myAllotments?.data || [];

  const [activeTab, setActiveTab] = useState<"occupation" | "withdrawal">((location.state as any)?.activeTab || "occupation");
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isFormVisible, setIsFormVisible] = useState((location.state as any)?.showForm || false);
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [selectedAllotment, setSelectedAllotment] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailType, setDetailType] = useState<"Occupation" | "Withdrawal" | null>(null);
  const [detailData, setDetailData] = useState<any>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [isUploadingDeclaration, setIsUploadingDeclaration] = useState(false);
  const [uploadedDeclarationData, setUploadedDeclarationData] = useState<any>(null);
  const declarationFileInputRef = React.useRef<HTMLInputElement>(null);

  const loginCategory = (() => {
    // 1. Check authState from getUserLoginData
    const authData = authState?.user?.data?.data || authState?.user?.data || authState?.user;
    const catFromAuth = getCategoryVal(authData?.user) || getCategoryVal(authData?.employee) || getCategoryVal(authData);
    if (catFromAuth) return catFromAuth;

    // 2. Check staffQuarterState.employeeDetails
    const empDetails = staffQuarterState?.employeeDetails?.data || staffQuarterState?.employeeDetails;
    const catFromEmpDetails = getCategoryVal(empDetails?.user) || getCategoryVal(empDetails?.employee) || getCategoryVal(empDetails);
    if (catFromEmpDetails) return catFromEmpDetails;

    // 3. Check localStorage "user"
    if (localUser) {
      const catFromLocal = getCategoryVal(localUser?.user) || getCategoryVal(localUser?.employee) || getCategoryVal(localUser);
      if (catFromLocal) return catFromLocal;
    }

    // 4. Check employeesState profile
    const empData = employeesState?.data?.data || employeesState?.data;
    const catFromEmp = getCategoryVal(empData?.user) || getCategoryVal(empData?.employee) || getCategoryVal(empData);
    if (catFromEmp) return catFromEmp;

    // 5. Check staffQuarter currentRegistration
    const regData = staffQuarterState?.currentRegistration?.data || staffQuarterState?.currentRegistration;
    const catFromReg = getCategoryVal(regData) || getCategoryVal(regData?.flatDetails) || getCategoryVal(regData?.employeeDetails);
    if (catFromReg) return catFromReg;

    // 6. Check selectedAllotment
    const catFromAllot = getCategoryVal(selectedAllotment);
    if (catFromAllot) return catFromAllot;

    // 7. Fallback
    return "-";
  })();

  const getDisplayCategory = (dataObj?: any) => {
    const res = resolveFlatCategory(
      dataObj || staffQuarterState?.currentRegistration?.data,
      selectedAllotment,
      allotments
    );
    if (res && res !== "-") return res;
    if (loginCategory && loginCategory !== "-") return loginCategory;
    return "-";
  };

  const allotteeName = (() => {
    const authData = authState?.user?.data?.data || authState?.user?.data || authState?.user;
    const empDetails = staffQuarterState?.employeeDetails?.data || staffQuarterState?.employeeDetails;
    const regData = staffQuarterState?.currentRegistration?.data || staffQuarterState?.currentRegistration;
    const empData = employeesState?.data?.data || employeesState?.data;

    return (
      authData?.employeeName || authData?.name || authData?.empName || authData?.user?.name || authData?.user?.employeeName ||
      empDetails?.employeeName || empDetails?.name || empDetails?.empName || empDetails?.user?.name || empDetails?.user?.employeeName ||
      regData?.employeeName || regData?.name || regData?.empName ||
      selectedAllotment?.employeeName || selectedAllotment?.name || selectedAllotment?.empName ||
      user?.name || user?.employeeName || user?.empName ||
      localUser?.name || localUser?.employeeName || localUser?.empName ||
      empData?.name || empData?.employeeName || empData?.empName ||
      "-"
    );
  })();

  const allotteeDesignation = (() => {
    const authData = authState?.user?.data?.data || authState?.user?.data || authState?.user;
    const empDetails = staffQuarterState?.employeeDetails?.data || staffQuarterState?.employeeDetails;
    const regData = staffQuarterState?.currentRegistration?.data || staffQuarterState?.currentRegistration;
    const empData = employeesState?.data?.data || employeesState?.data;

    return (
      authData?.designation || authData?.designationName || authData?.designation_name || authData?.user?.designation ||
      empDetails?.designation || empDetails?.designationName || empDetails?.designation_name || empDetails?.user?.designation ||
      regData?.designation || regData?.designationName || regData?.designation_name ||
      selectedAllotment?.designation || selectedAllotment?.designationName || selectedAllotment?.designation_name ||
      user?.designation || user?.designationName || user?.desg ||
      localUser?.designation || localUser?.designationName || localUser?.desg ||
      empData?.designation || empData?.designationName || empData?.desg ||
      "-"
    );
  })();

  const allotteeEmpNo = (() => {
    const authData = authState?.user?.data?.data || authState?.user?.data || authState?.user;
    const empDetails = staffQuarterState?.employeeDetails?.data || staffQuarterState?.employeeDetails;
    const regData = staffQuarterState?.currentRegistration?.data || staffQuarterState?.currentRegistration;
    const empData = employeesState?.data?.data || employeesState?.data;

    return (
      authData?.employeeNumber || authData?.employeeId || authData?.empId || authData?.user?.employeeNumber ||
      empDetails?.employeeNumber || empDetails?.employeeId || empDetails?.empId || empDetails?.user?.employeeNumber ||
      regData?.employeeNumber || regData?.employeeId || regData?.empId ||
      selectedAllotment?.employeeNumber || selectedAllotment?.employeeId || selectedAllotment?.empId ||
      user?.empId || user?.employeeId || user?.employeeNumber ||
      localUser?.employeeNumber || localUser?.employeeId || localUser?.empId ||
      empData?.employeeNumber || empData?.employeeId || empData?.empId ||
      "-"
    );
  })();

  const handleGenerateDeclarationPdf = (regId?: any) => {
    setIsGeneratingPdf(true);
    toast.info("Fetching registration details...");

    dispatch(getStaffQuarterCurrentRegistration({})).then((regRes: any) => {
      const data = regRes.payload?.data || regRes.payload;
      const targetRegId = regId || data?.registrationId || data?.id || staffQuarterState?.currentRegistration?.data?.registrationId || staffQuarterState?.currentRegistration?.data?.id || selectedAllotment?.registrationId || selectedAllotment?.id || 1;
      const parsedId = Number(targetRegId);

      toast.info(`Generating Declaration Agreement PDF for Registration ID: ${parsedId}...`);
      dispatch(generateDeclarationPdf({ registrationId: parsedId }))
        .then((res: any) => {
          setIsGeneratingPdf(false);
          if (res.payload && !(res.payload instanceof Blob && res.payload.size === 0)) {
            const blob = res.payload instanceof Blob ? res.payload : new Blob([res.payload], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Declaration_Agreement_${parsedId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Declaration Agreement PDF downloaded successfully!");
          } else {
            toast.error("Failed to generate Declaration Agreement PDF");
          }
        })
        .catch((err: any) => {
          setIsGeneratingPdf(false);
          toast.error("An error occurred while generating Declaration Agreement PDF");
        });
    }).catch(() => {
      setIsGeneratingPdf(false);
      toast.error("Could not fetch registration details");
    });
  };

  const [isGeneratingReportPdf, setIsGeneratingReportPdf] = useState(false);

  const handleGenerateOccupationReportPdf = (regId?: any) => {
    setIsGeneratingReportPdf(true);
    toast.info("Fetching registration details...");

    dispatch(getStaffQuarterCurrentRegistration({})).then((regRes: any) => {
      const data = regRes.payload?.data || regRes.payload;
      const targetRegId = regId || data?.registrationId || data?.id || staffQuarterState?.currentRegistration?.data?.registrationId || staffQuarterState?.currentRegistration?.data?.id || selectedAllotment?.registrationId || selectedAllotment?.id || 1;
      const parsedId = Number(targetRegId);

      toast.info(`Generating Occupation Report PDF for Registration ID: ${parsedId}...`);
      dispatch(generateOccupationReportPdf({ registrationId: parsedId }))
        .then((res: any) => {
          setIsGeneratingReportPdf(false);
          if (res.payload && !(res.payload instanceof Blob && res.payload.size === 0)) {
            const blob = res.payload instanceof Blob ? res.payload : new Blob([res.payload], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Occupation_Report_${parsedId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Occupation Report PDF downloaded successfully!");
          } else {
            toast.error("Failed to generate Occupation Report PDF");
          }
        })
        .catch((err: any) => {
          setIsGeneratingReportPdf(false);
          toast.error("An error occurred while generating Occupation Report PDF");
        });
    }).catch(() => {
      setIsGeneratingReportPdf(false);
      toast.error("Could not fetch registration details");
    });
  };

  const handleDeclarationFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;
      const regId = Number(selectedAllotment?.registrationId || staffQuarterState?.currentRegistration?.data?.id || selectedAllotment?.id || 123);
      const occId = Number(selectedAllotment?.occupationId || selectedAllotment?.id || staffQuarterState?.currentRegistration?.data?.occupationId || 456);

      setIsUploadingDeclaration(true);
      dispatch(
        uploadDeclarationSignature({
          registrationId: regId,
          occupationId: occId,
          signature: base64String,
          fileName: file.name
        })
      )
        .then((res: any) => {
          setIsUploadingDeclaration(false);
          if (res.payload?.success || res.payload?.data) {
            const resData = res.payload?.data || res.payload;
            setUploadedDeclarationData(resData);
            toast.success(res.payload?.message || "Declaration signature uploaded successfully");
          } else {
            toast.error(res.payload?.message || "Failed to upload declaration signature");
          }
        })
        .catch((err: any) => {
          setIsUploadingDeclaration(false);
          toast.error("An error occurred while uploading declaration signature");
        });
    };
    reader.onerror = () => {
      toast.error("Failed to read selected file");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const fetchHistory = () => {
    dispatch(getStaffQuarterOccupationWithdrawalList({})).then((res: any) => {
      if (res.payload?.success) {
        const occs = (res.payload.data?.occupations || []).map((o: any) => ({ ...o, type: "Occupation" }));
        const wids = (res.payload.data?.withdrawals || []).map((w: any) => ({ ...w, type: "Withdrawal" }));
        const combined = [...occs, ...wids].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setHistoryList(combined);
      }
    });
  };

  useEffect(() => {
    if (allotments.length > 0 && !selectedAllotment) {
      setSelectedAllotment(allotments[0]);
    }
  }, [allotments, selectedAllotment]);

  useEffect(() => {
    if (location.state) {
      const state = location.state as any;
      if (state.activeTab) setActiveTab(state.activeTab);
      if (state.showForm !== undefined) setIsFormVisible(state.showForm);
    }
  }, [location.state]);
  const [remarks, setRemarks] = useState("");
  const [filterType, setFilterType] = useState<"All" | "Occupation" | "Withdrawal">("All");

  // Occupation form statew3uq
  const [occupationForm, setOccupationForm] = useState({
    dateOfOccupation: "",
    acceptTerms: false,
  });

  // Family members list
  const [members, setMembers] = useState<Array<{ id: string; name: string; relation: string; photo?: File | null }>>([
    { id: Math.random().toString(36).substring(7), name: "", relation: "", photo: null },
  ]);

  // Withdrawal form state
  const [withdrawalForm, setWithdrawalForm] = useState({
    allotmentRef: "",
    reason: "",
    remarks: "",
  });

  useEffect(() => {
    dispatch(getUserLoginData({}));
    dispatch(getMyProfile({}));
    dispatch(getEmployeeDetailsForStaffQuarter({}));
    dispatch(getUserStaffQuarterRequests({}));
    dispatch(getMyAllotments({}));
    dispatch(getStaffQuarterCurrentRegistration({}));
    fetchHistory();
    const t = setTimeout(() => setIsLoadingProfile(false), 8000);
    return () => clearTimeout(t);
  }, [dispatch]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result?.toString().split(',')[1];
        resolve(base64String || "");
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleOccupationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as any;
    if (type === "checkbox") {
      setOccupationForm((p) => ({ ...p, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setOccupationForm((p) => ({ ...p, [name]: value }));
    }
  };

  const handleWithdrawalChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target as any;
    setWithdrawalForm((p) => ({ ...p, [name]: value }));
  };
  const addMember = () => setMembers((m) => [...m, { id: Math.random().toString(36).substring(7), name: "", relation: "", photo: null }]);
  const removeMember = (i: number) => setMembers((m) => m.filter((_, idx) => idx !== i));
  const updateMember = (i: number, key: string, value: any) => setMembers((m) => m.map((mm, idx) => (idx === i ? { ...mm, [key]: value } : mm)));

  const handleFileChange = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Invalid file type. Only JPG, JPEG, PNG, SVG, and PDF are allowed.");
        e.target.value = '';
        updateMember(i, "photo", null);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size exceeds 5MB limit.");
        e.target.value = '';
        updateMember(i, "photo", null);
        return;
      }
    }
    updateMember(i, "photo", file);
  };

  const submitOccupation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAllotment) {
      toast.error("Please select an allotment reference");
      return;
    }
    if (!occupationForm.dateOfOccupation) {
      toast.error("Please select date of occupation");
      return;
    }
    if (!occupationForm.acceptTerms) {
      toast.error("Please accept the terms and conditions");
      return;
    }

    try {
      const familyDetails = await Promise.all(
        members.map(async (m) => {
          let base64 = "";
          let extension = "";
          if (m.photo) {
            base64 = await fileToBase64(m.photo);
            extension = m.photo.name.split('.').pop() || "";
          }
          return {
            memberName: m.name,
            relationship: m.relation,
            documentName: m.photo?.name || "",
            fileExtension: extension,
            base64: base64
          };
        })
      );

      const currentReg = staffQuarterState?.currentRegistration?.data;
      const payload = {
        allotmentReferenceNumber: String(
          currentReg?.allotmentReferenceNumber ||
          currentReg?.referenceNumber ||
          selectedAllotment?.referenceNumber ||
          selectedAllotment?.registrationId ||
          ""
        ),
        occupationDate: new Date(occupationForm.dateOfOccupation).toISOString(),
        isTermsAccepted: occupationForm.acceptTerms,
        remarks: remarks || "",
        familyDetails: familyDetails.filter(f => f.memberName)
      };

      dispatch(submitStaffQuarterOccupation(payload)).then((res: any) => {
        if (res.payload?.success || res.payload?.status === 200) {
          toast.success(res.payload?.message || "Occupation submitted successfully");
          setIsFormVisible(false);
          fetchHistory();
        } else {
          const errorMessage = typeof res.payload === 'string' ? res.payload : (res.payload?.message || res.error?.message || "Failed to submit occupation");
          toast.error(errorMessage);
        }
      });
    } catch (err: any) {
      toast.error(err?.message || "Error processing files");
    }
  };

  const submitWithdrawal = (e: React.FormEvent) => {
    e.preventDefault();

    const currentReg = staffQuarterState?.currentRegistration?.data;
    const refNumber = String(
      currentReg?.referenceNumber ||
      currentReg?.registrationReferenceNumber ||
      selectedAllotment?.referenceNumber ||
      selectedAllotment?.registrationReferenceNumber ||
      selectedAllotment?.registrationId ||
      ""
    );

    if (!refNumber || refNumber === "undefined") {
      toast.error("No registration reference available to withdraw");
      return;
    }
    if (!withdrawalForm.reason) {
      toast.error("Please provide reason for withdrawal");
      return;
    }

    const payload = {
      registrationReferenceNumber: refNumber,
      reason: withdrawalForm.reason,
      remarks: withdrawalForm.remarks || ""
    };

    dispatch(withdrawStaffQuarterOccupation(payload)).then((res: any) => {
      if (res.payload?.success || res.payload?.status === 200) {
        toast.success(res.payload?.message || "Withdrawal request submitted successfully");
        setIsFormVisible(false);
        fetchHistory();
      } else {
        const errorMessage = typeof res.payload === 'string' ? res.payload : (res.payload?.message || res.error?.message || "Failed to submit withdrawal request");
        toast.error(errorMessage);
      }
    });
  };

  const DashboardTile = ({
    title,
    icon,
    count,
    colorClass = "text-primary",
  }: {
    title: string;
    icon?: string;
    count?: string | number;
    colorClass?: string;
  }) => {
    return (
      <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-6 col-sm-6 mb-3">
        <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: "16px", backgroundColor: "#fff" }}>
          <div className="card-body d-flex align-items-center p-3">
            <div className={`d-flex align-items-center justify-content-center me-3 rounded-circle`} style={{ width: "48px", height: "48px", backgroundColor: "rgba(var(--bs-primary-rgb), 0.03)" }}>
              {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "24px" }}></i>}
            </div>
            <div>
              <div className="text-muted small mb-1" style={{ fontSize: "12px", letterSpacing: "0.2px" }}>{title}</div>
              {count !== undefined && <div className={`h4 fw-bold mb-0 ${colorClass}`}>{count}</div>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const formatDate = (date: any) => {
    if (!date) return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Occupation / Withdrawal</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={routes.employeeDashboard || "#"}>Dashboard</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Occupation & Withdrawal</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            {isFormVisible ? (
              <button className="btn btn-outline-secondary" onClick={() => setIsFormVisible(false)}>
                <i className="ti ti-arrow-left me-1"></i>
                Back to List
              </button>
            ) : (
              <button className="btn btn-danger" onClick={() => setIsFormVisible(true)}>
                <i className="ti ti-plus me-1"></i>
                New Request
              </button>
            )}
          </div>
        </div>

        {!isFormVisible && (
          <>

            <div className="card shadow-sm border-0" style={{ borderRadius: '12px' }}>
              <div className="card-header bg-white py-3 border-bottom-0 d-flex justify-content-between align-items-center" style={{ borderRadius: '12px 12px 0 0' }}>
                <h5 className="card-title mb-0">Requests History</h5>
                <div className="d-flex bg-light p-1 rounded-pill" style={{ border: '1px solid #e2e8f0' }}>
                  {/* <button
                    type="button"
                    className={`btn btn-sm rounded-pill border-0 fw-semibold px-4 ${filterType === 'All' ? 'bg-white shadow-sm text-dark' : 'text-muted bg-transparent'}`}
                    onClick={() => setFilterType('All')}
                    style={{ transition: 'all 0.2s ease', fontSize: '0.85rem' }}
                  >
                    All
                  </button> */}
                  <button
                    type="button"
                    className={`btn btn-sm rounded-pill border-0 fw-semibold px-4 mx-1 ${filterType === 'Occupation' ? 'bg-white shadow-sm text-dark' : 'text-muted bg-transparent'}`}
                    onClick={() => setFilterType('Occupation')}
                    style={{ transition: 'all 0.2s ease', fontSize: '0.85rem' }}
                  >
                    Occupation
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm rounded-pill border-0 fw-semibold px-4 ${filterType === 'Withdrawal' ? 'bg-white shadow-sm text-dark' : 'text-muted bg-transparent'}`}
                    onClick={() => setFilterType('Withdrawal')}
                    style={{ transition: 'all 0.2s ease', fontSize: '0.85rem' }}
                  >
                    Withdrawal
                  </button>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive" style={{ minHeight: '200px' }}>
                  <table className="table table-hover table-center mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th>Refrence Number</th>
                        <th>Type</th>
                        <th>Request Date</th>
                        <th>Occupation/Withdrawal Date</th>
                        <th className="text-center">Status</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyList.filter((req: any) => filterType === 'All' || req.type === filterType).length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-4 text-muted">No requests found</td>
                        </tr>
                      ) : (
                        historyList.filter((req: any) => filterType === 'All' || req.type === filterType).map((req: any, idx: number) => (
                          <tr key={idx}>
                            <td className="fw-bold text-dark">{req.referenceNumber || "-"}</td>
                            <td>{req.type || "Occupation"}</td>
                            <td>{formatDate(req.createdAt)}</td>
                            <td>{formatDate(req.occupationDate || req.withdrawalDate || (req.type === "Withdrawal" ? req.createdAt : ""))}</td>
                            <td className="text-center">
                              <span className={`badge ${req.status === 'APPROVED' ? 'bg-success' : req.status === 'REJECTED' ? 'bg-danger' : 'bg-warning text-white'}`}>
                                {req.status || "PENDING"}
                              </span>
                            </td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-outline-danger me-1 d-inline-flex align-items-center gap-1"
                                onClick={() => handleGenerateDeclarationPdf(req.registrationId || req.id)}
                                disabled={isGeneratingPdf}
                                title="Download Declaration Agreement PDF"
                              >
                                <i className="ti ti-file-download"></i>
                                <span>Declaration</span>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-info me-1 d-inline-flex align-items-center gap-1"
                                onClick={() => handleGenerateOccupationReportPdf(req.registrationId || req.id)}
                                disabled={isGeneratingReportPdf}
                                title="Download Occupation Report PDF"
                              >
                                <i className="ti ti-file-text"></i>
                                <span>Report</span>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  navigate(routes.staffQuarter.details.replace(":id", encodeRouteParam(req.registrationId || req.referenceNumber)));
                                }}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {isFormVisible && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="btn-group p-1 mb-4" role="tablist">
                <button className={`btn ${activeTab === "occupation" ? "btn-light" : "btn-outline-light"}`} onClick={() => setActiveTab("occupation")}>
                  Initiate Occupation
                </button>
                <button className={`btn ${activeTab === "withdrawal" ? "btn-light" : "btn-outline-light"}`} onClick={() => setActiveTab("withdrawal")}>
                  Request Withdrawal
                </button>
              </div>

              {activeTab === "occupation" ? (
                <form onSubmit={submitOccupation}>
                  <h5 className="mb-3">Occupation Details</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label text-muted small fw-bold">Allotment Reference *</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={selectedAllotment?.referenceNumber || selectedAllotment?.registrationReferenceNumber || selectedAllotment?.registrationId || ""}
                        readOnly
                        disabled
                        placeholder="No allotment reference available"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Date of Occupation *</label>
                      <DatePicker
                        format="DD/MM/YYYY"
                        placeholder="dd/mm/yyyy"
                        className="form-control"
                        value={occupationForm.dateOfOccupation ? dayjs(occupationForm.dateOfOccupation) : null}
                        disabledDate={(current) => {
                          if (!current) return false;
                          const rawDate = staffQuarterState?.currentRegistration?.data?.allotmentDate || selectedAllotment?.allotmentDate || selectedAllotment?.createdAt;
                          const today = dayjs().endOf('day');
                          if (rawDate) {
                            const allotmentDate = dayjs(rawDate);
                            if (allotmentDate.isValid()) {
                              return current.isBefore(allotmentDate.startOf('day')) || current.isAfter(today);
                            }
                          }
                          return current.isAfter(today);
                        }}
                        onChange={(date, dateString) => setOccupationForm((p) => ({ ...p, dateOfOccupation: date ? date.toISOString() : "" }))}
                      />
                    </div>
                    {staffQuarterState?.currentRegistration?.data && (
                      <div className="col-12 mb-3">
                        <div className="p-3 bg-light rounded border border-dashed border-primary" style={{ backgroundColor: 'rgba(var(--bs-primary-rgb), 0.02)' }}>
                          <h6 className="mb-2 text-primary fw-bold d-flex align-items-center">
                            <i className="ti ti-info-circle me-2"></i> Current Registration Details
                          </h6>
                          <div className="row g-3">
                            <div className="col-md-4">
                              <Text type="secondary" style={{ fontSize: '12px' }}>Registration Ref</Text>
                              <div className="fw-bold">{staffQuarterState.currentRegistration.data.referenceNumber}</div>
                            </div>
                            <div className="col-md-4">
                              <Text type="secondary" style={{ fontSize: '12px' }}>Allotment Ref</Text>
                              <div className="fw-bold">{staffQuarterState.currentRegistration.data.allotmentReferenceNumber || "N/A"}</div>
                            </div>
                            <div className="col-md-4">
                              <Text type="secondary" style={{ fontSize: '12px' }}>Flat Number</Text>
                              <div className="fw-bold">{formatFlatNoWithBlock(staffQuarterState.currentRegistration.data) || "N/A"}</div>
                            </div>
                            <div className="col-md-4">
                              <Text type="secondary" style={{ fontSize: '12px' }}>Allotment Date</Text>
                              <div className="fw-bold">{formatDate(staffQuarterState.currentRegistration.data.allotmentDate)}</div>
                            </div>
                            <div className="col-md-4">
                              <Text type="secondary" style={{ fontSize: '12px' }}>Category</Text>
                              <div className="fw-bold">{getDisplayCategory(staffQuarterState.currentRegistration.data)}</div>
                            </div>
                            {/* <div className="col-md-4">
                              <Text type="secondary" style={{ fontSize: '12px' }}>Status</Text>
                              <div><span className="badge bg-success-light text-success">{staffQuarterState.currentRegistration.data.allotmentStatus || staffQuarterState.currentRegistration.data.status}</span></div>
                            </div> */}
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedAllotment && !staffQuarterState?.currentRegistration?.data && (
                      <div className="col-12 mb-3">
                        <div className="p-3 bg-light rounded border border-dashed">
                          <div className="row">
                            <div className="col-md-3"><strong>Flat:</strong> {formatFlatNoWithBlock(selectedAllotment) || "N/A"}</div>
                            <div className="col-md-3"><strong>Type:</strong> {selectedAllotment.quarterType}</div>
                            <div className="col-md-3"><strong>Category:</strong> {getDisplayCategory(selectedAllotment)}</div>
                            <div className="col-md-3"><strong>Location:</strong> {selectedAllotment.location}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="col-12 mb-3">
                      <div className="form-check p-3 bg-light rounded-3 border">
                        <input
                          className="form-check-input mt-1 me-2"
                          type="checkbox"
                          id="acceptTerms"
                          name="acceptTerms"
                          checked={occupationForm.acceptTerms}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setShowDeclarationModal(true);
                            } else {
                              setOccupationForm((p) => ({ ...p, acceptTerms: false }));
                            }
                          }}
                          style={{ width: "1.25em", height: "1.25em", cursor: "pointer" }}
                        />
                        <label
                          className="form-check-label text-dark fw-semibold small"
                          htmlFor="acceptTerms"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.preventDefault();
                            setShowDeclarationModal(true);
                          }}
                        >
                          I accept the terms and conditions for occupation of the allotted staff quarter and agree to pay the prescribed license fee and other charges as per DMRC policy.
                          <span className="text-danger ms-1 fw-bold">(Click to View & Accept Declaration / Agreement)</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="card mt-3 mb-3">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <div>Family Details</div>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={addMember}>+ Add Member</button>
                    </div>
                    <div className="card-body">
                      {members.map((m, i) => (
                        <div key={m.id} className="d-flex gap-2 align-items-center mb-2">
                          <input placeholder="Member Name" className="form-control" value={m.name} onChange={(e) => updateMember(i, "name", e.target.value)} />
                          <select className="form-select" value={m.relation} onChange={(e) => updateMember(i, "relation", e.target.value)}>
                            <option value="">Select</option>
                            <option>Spouse</option>
                            <option>Child</option>
                            <option>Sibling</option>
                            <option>Parent</option>
                          </select>
                          <div className="position-relative" style={{ minWidth: '220px', flex: 1 }}>
                            <input
                              id={`file-input-${m.id}`}
                              type="file"
                              className="form-control"
                              accept=".jpg,.jpeg,.png,.svg,.pdf"
                              onChange={(e) => handleFileChange(i, e)}
                              style={{ paddingRight: m.photo ? '35px' : '12px' }}
                            />
                            {m.photo && (
                              <button
                                type="button"
                                className="btn btn-sm position-absolute top-50 translate-middle-y end-0 me-1 p-0 d-flex align-items-center justify-content-center"
                                onClick={() => {
                                  updateMember(i, "photo", null);
                                  const input = document.getElementById(`file-input-${m.id}`) as HTMLInputElement;
                                  if (input) input.value = '';
                                }}
                                title="Remove attachment"
                                style={{ width: '24px', height: '24px', background: 'rgba(220, 53, 69, 0.1)', color: '#dc3545', border: 'none', borderRadius: '50%', zIndex: 10 }}
                              >
                                <i className="ti ti-x" style={{ fontSize: '14px' }}></i>
                              </button>
                            )}
                          </div>
                          <button type="button" className="btn btn-outline-danger" onClick={() => removeMember(i)}>Remove</button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Remarks (Optional)</label>
                    <textarea className="form-control" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)}></textarea>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setIsFormVisible(false)}>Cancel</button>
                    <button type="submit" className="btn btn-danger">Submit Occupation</button>
                  </div>
                </form>
              ) : (
                <form onSubmit={submitWithdrawal}>
                  <h5 className="mb-3">Withdrawal Details</h5>
                  <div className="row">
                    {staffQuarterState?.currentRegistration?.data && (
                      <div className="col-12 mb-3">
                        <div className="p-3 bg-light rounded border border-dashed border-primary" style={{ backgroundColor: 'rgba(var(--bs-primary-rgb), 0.02)' }}>
                          <h6 className="mb-2 text-primary fw-bold d-flex align-items-center">
                            <i className="ti ti-info-circle me-2"></i> Current Registration Details
                          </h6>
                          <div className="row g-3">
                            <div className="col-md-4">
                              <Text type="secondary" style={{ fontSize: '12px' }}>Registration Ref</Text>
                              <div className="fw-bold">{staffQuarterState.currentRegistration.data.referenceNumber}</div>
                            </div>
                            <div className="col-md-4">
                              <Text type="secondary" style={{ fontSize: '12px' }}>Allotment Ref</Text>
                              <div className="fw-bold">{staffQuarterState.currentRegistration.data.allotmentReferenceNumber || "N/A"}</div>
                            </div>
                            <div className="col-md-4">
                              <Text type="secondary" style={{ fontSize: '12px' }}>Flat Number</Text>
                              <div className="fw-bold">{formatFlatNoWithBlock(staffQuarterState.currentRegistration.data) || "N/A"}</div>
                            </div>
                            <div className="col-md-4">
                              <Text type="secondary" style={{ fontSize: '12px' }}>Allotment Date</Text>
                              <div className="fw-bold">{formatDate(staffQuarterState.currentRegistration.data.allotmentDate)}</div>
                            </div>
                            <div className="col-md-4">
                              <Text type="secondary" style={{ fontSize: '12px' }}>Category</Text>
                              <div className="fw-bold">{getDisplayCategory(staffQuarterState.currentRegistration?.data)}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="col-12 mb-3">
                      <label className="form-label">Reason for Withdrawal *</label>
                      <textarea name="reason" value={withdrawalForm.reason} onChange={handleWithdrawalChange} className="form-control" rows={3} placeholder="Please provide a detailed reason for withdrawal"></textarea>
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">Additional Remarks (Optional)</label>
                      <textarea name="remarks" value={withdrawalForm.remarks} onChange={handleWithdrawalChange} className="form-control" rows={2} placeholder="Enter any additional remarks"></textarea>
                    </div>
                  </div>
                  <div className="alert alert-warning">
                    <strong>Important:</strong> Withdrawal of allotment is irreversible.
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setIsFormVisible(false)}>Cancel</button>
                    <button type="submit" className="btn btn-danger">Submit Withdrawal Request</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Declaration / Agreement Modal */}
      {showDeclarationModal && (
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 99999 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: "16px" }}>
              <div className="modal-header bg-white py-3 border-bottom" style={{ borderRadius: "16px 16px 0 0" }}>
                <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                  <i className="ti ti-file-certificate text-danger fs-4"></i>
                  DECLARATION / AGREEMENT
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeclarationModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4" style={{ maxHeight: "65vh", overflowY: "auto", fontSize: "14px", lineHeight: "1.7" }}>
                <div className="p-3 bg-light rounded-3 border mb-3">
                  <p className="fw-semibold mb-3 text-dark">
                    1. I accept the allotment of DMRC staff quarter no. <strong>{formatFlatNoWithBlock(selectedAllotment, selectedAllotment?.block) !== "-" ? formatFlatNoWithBlock(selectedAllotment, selectedAllotment?.block) : (formatFlatNoWithBlock(staffQuarterState?.currentRegistration?.data, staffQuarterState?.currentRegistration?.data?.block) !== "-" ? formatFlatNoWithBlock(staffQuarterState?.currentRegistration?.data, staffQuarterState?.currentRegistration?.data?.block) : "____")}</strong> at <strong>{selectedAllotment?.location || staffQuarterState?.currentRegistration?.data?.location || "____"}</strong> as per the Office Order No. <strong>{selectedAllotment?.referenceNumber || staffQuarterState?.currentRegistration?.data?.allotmentReferenceNumber || "____"}</strong>.
                  </p>
                  <p className="mb-3">
                    2. I and my family residing with me agree to abide by the DMRC Housing Allotment Rules as amended and in force from time to time.
                  </p>
                  <p className="mb-3">
                    3. I agree to abide by directions of RRA with respect to staff quarters.
                  </p>
                  <p className="mb-3">
                    4. I agree to pay the license fee and other dues for the quarter allotted to me at such rates as may be fixed by the Corporation from time to time and the same may be recovered from my monthly salary.
                  </p>
                  <p className="mb-3">
                    5. I shall be personally liable for the payment of license fee for the quarter allotted to me and for any damages beyond fair wear and tear caused thereto or to the furniture, fixtures or fittings, etc. during the period of the quarter remains allotted to me and until it is physically vacated by me.
                  </p>
                  <p className="mb-3">
                    6. I agree to pay the electricity, water and other charges levied by the concerned authorities and I shall be personally responsible for any loss sustained by the Corporation due to non-payment of bills etc.
                  </p>
                  <p className="mb-3">
                    7. I agree to keep my surrounding common premises clean and free of personal items.
                  </p>
                  <p className="mb-3">
                    8. I shall not sublet or share any portion of the quarter allotted to me. The servant quarters and garages if any, attached to the flat shall be used only for the bonafide purpose or such other purposes as may be permitted by the Corporation.
                  </p>
                  <p className="mb-3">
                    9. I shall not make any addition/alteration to the premises allotted to me without the prior permission of the Corporation.
                  </p>
                  <p className="mb-3">
                    10. I agree to vacate the quarter on cancellation of the allotment or when the allotment deemed to be cancelled and in the event of my or my family's continued occupation of the quarter. I agree to pay penal rent as per rules with effect from the date of cancellation of the allotment.
                  </p>
                  <hr className="my-3" />
                  <div className="row g-4 pt-2">
                    {/* Left Column: Place & Date */}
                    <div className="col-md-6 col-12 d-flex flex-column justify-content-between gap-3">
                      <div>
                        <span className="text-muted d-block small mb-1">Place:</span>
                        <strong className="text-dark" style={{ fontSize: '15px' }}>{selectedAllotment?.location || staffQuarterState?.currentRegistration?.data?.location || "GURGAON"}</strong>
                      </div>
                      <div>
                        <span className="text-muted d-block small mb-1">Date:</span>
                        <strong className="text-dark" style={{ fontSize: '15px' }}>{new Date().toLocaleDateString('en-GB')}</strong>
                      </div>
                    </div>

                    {/* Right Column: Signature Top, Employee Details Below */}
                    <div className="col-md-6 col-12 d-flex flex-column align-items-md-end">
                      {/* Signature Area at Top */}
                      <div className="text-center mb-4" style={{ minWidth: '200px' }}>
                        {uploadedDeclarationData?.signatureUrl || uploadedDeclarationData?.signedUrl || uploadedDeclarationData?.imageBase64 || uploadedDeclarationData?.signatureBase64 || uploadedDeclarationData?.base64Image || uploadedDeclarationData?.base64 ? (
                          <div className="mb-2">
                            <img
                              src={uploadedDeclarationData?.signatureUrl || uploadedDeclarationData?.signedUrl || uploadedDeclarationData?.imageBase64 || uploadedDeclarationData?.signatureBase64 || uploadedDeclarationData?.base64Image || uploadedDeclarationData?.base64}
                              alt="Uploaded Signature"
                              style={{ maxHeight: '60px', maxWidth: '180px', objectFit: 'contain' }}
                            />
                          </div>
                        ) : (
                          <div style={{ width: 200, borderBottom: '1.5px solid #475569', marginBottom: 6 }} className="ms-md-auto ms-0"></div>
                        )}
                        <span className="text-muted d-block small fw-semibold">(Signature of allottee)</span>
                        {uploadedDeclarationData?.fileName && (
                          <small className="text-success d-block mt-1" style={{ fontSize: '11px' }}>
                            <i className="ti ti-circle-check me-1"></i>
                            {uploadedDeclarationData.fileName}
                          </small>
                        )}
                      </div>

                      {/* Employee Details Below Signature */}
                      <div className="d-flex flex-column gap-2 text-md-end text-start">
                        <div>
                          <span className="text-muted small me-2">Name:</span>
                          <strong className="text-dark">{allotteeName}</strong>
                        </div>
                        <div>
                          <span className="text-muted small me-2">Emp. No.:</span>
                          <strong className="text-dark">{allotteeEmpNo}</strong>
                        </div>
                        <div>
                          <span className="text-muted small me-2">Designation:</span>
                          <strong className="text-dark">{allotteeDesignation}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-light py-3 border-top d-flex justify-content-between align-items-center" style={{ borderRadius: "0 0 16px 16px" }}>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-danger fw-bold d-inline-flex align-items-center gap-1 shadow-sm"
                    onClick={() => handleGenerateDeclarationPdf(selectedAllotment?.registrationId || staffQuarterState?.currentRegistration?.data?.id || selectedAllotment?.id || 1)}
                    disabled={isGeneratingPdf}
                  >
                    {isGeneratingPdf ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Downloading PDF...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-download me-1"></i>
                        Download Declaration PDF
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger fw-bold d-inline-flex align-items-center gap-1 shadow-sm"
                    onClick={() => declarationFileInputRef.current?.click()}
                    disabled={isUploadingDeclaration}
                  >
                    {isUploadingDeclaration ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-upload me-1"></i>
                        Upload Signed Decleration
                      </>
                    )}
                  </button>
                  <input
                    type="file"
                    ref={declarationFileInputRef}
                    style={{ display: "none" }}
                    accept="image/*,.pdf"
                    onChange={handleDeclarationFileUpload}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-3"
                    onClick={() => {
                      setOccupationForm((p) => ({ ...p, acceptTerms: false }));
                      setShowDeclarationModal(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger px-4 fw-bold shadow-sm"
                    onClick={() => {
                      setOccupationForm((p) => ({ ...p, acceptTerms: true }));
                      setShowDeclarationModal(false);
                      toast.success("Declaration / Agreement accepted.");
                    }}
                  >
                    <i className="ti ti-check me-1"></i>
                    I Accept & Agree
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDeclarationModal && <div className="modal-backdrop fade show" style={{ zIndex: 99990 }} onClick={() => setShowDeclarationModal(false)}></div>}

      <style>{`
        .hover-card:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important; }
        .rounded-6 { border-radius: 6px; }
      `}</style>
    </div>
  );
};

export default OccupationWithdrawal;
